import axios from 'axios';

// ✅ Get the API base URL dynamically
const getBackendURL = () => {
    // In local dev (npm start), the backend runs standalone on port 5000 -
    // there's no reverse proxy, so we must address it directly.
    if (process.env.NODE_ENV === 'development') {
        const hostname = window.location.hostname;
        if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
            return `http://${hostname}:5000/api`;
        }
        return 'http://localhost:5000/api';
    }
    // In production (npm run build), the app is served behind Nginx, which
    // reverse-proxies /api on the SAME origin to the backend - so we call it
    // as a same-origin relative path rather than hardcoding a host at all.
    // This also makes the build portable across domains with no rebuild needed.
    return `${window.location.origin}/api`;
};

// ✅ Base URL for static files (uploads/posters/reports) - same host as the
// API but without the /api suffix, so it works whether the app is opened via
// localhost or over the LAN (unlike a hardcoded "http://localhost:5000").
export const getStaticBaseUrl = () => getBackendURL().replace(/\/api\/?$/, '');

const api = axios.create({
    baseURL: getBackendURL(),
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 30000
});

const isDev = process.env.NODE_ENV === 'development';

// ✅ Log the API URL for debugging (dev only)
if (isDev) console.log('📡 API Base URL:', api.defaults.baseURL);

// Request interceptor to add token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // ✅ Per-request logging is dev-only - it ran on every single API call
        // in production too, adding real overhead and cluttering the console.
        if (isDev) {
            console.log('📤 API Request:', {
                method: config.method,
                url: config.url,
                baseURL: config.baseURL
            });
        }
        return config;
    },
    (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => {
        if (isDev) {
            console.log('📥 API Response:', {
                status: response.status,
                url: response.config.url
            });
        }
        return response;
    },
    (error) => {
        console.error('❌ Response Error:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });
        
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export default api;